import { useEffect, useState } from "react";
import { Landmark, LockKeyhole, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";

const PROFILES = {
  domestic: [
    ["beneficiary_name", "Beneficiary name"],
    ["routing_number", "Routing number"],
    ["account_number", "Account number"],
  ],
  international: [
    ["beneficiary_name", "Beneficiary name"],
    ["business_address", "Business address"],
    ["account_number", "Account number"],
    ["bank_name", "Bank name"],
    ["bank_address", "Bank address"],
    ["swift_code", "SWIFT code"],
  ],
};

const blank = () => Object.fromEntries(Object.keys(PROFILES).flatMap((type) => PROFILES[type].map(([key]) => [key, ""])));

export function BankDetailsAdmin() {
  const [bankType, setBankType] = useState("domestic");
  const [values, setValues] = useState(blank);
  const [configured, setConfigured] = useState({});
  const [recipient, setRecipient] = useState({ full_name: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/bank-details/profiles").then(({ data }) => setConfigured(data)).catch((error) => toast.error(errText(error)));
  }, []);

  const saveProfile = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const details = Object.fromEntries(PROFILES[bankType].map(([key]) => [key, values[key]]));
      await api.put(`/bank-details/profiles/${bankType}`, details);
      setConfigured((current) => ({ ...current, [bankType]: true }));
      setValues(blank());
      toast.success("Bank details saved");
    } catch (error) {
      toast.error(errText(error));
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/bank-details/links", { bank_type: bankType, ...recipient });
      toast.success(`Secure link sent to ${recipient.email}`);
      setRecipient({ full_name: "", email: "" });
    } catch (error) {
      toast.error(errText(error));
    } finally {
      setBusy(false);
    }
  };

  const fieldClass = "mt-1 block w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E62314]";

  return (
    <section className="max-w-3xl space-y-10" data-testid="bank-details-admin">
      <div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Bank details type">
          {Object.keys(PROFILES).map((type) => (
            <button key={type} type="button" onClick={() => setBankType(type)}
              className={`border px-4 py-2 text-sm font-bold capitalize ${bankType === type ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-black/20 bg-white hover:border-black"}`}
              data-testid={`bank-details-type-${type}`}>
              {type === "domestic" ? "Domestic · US" : "International"}
            </button>
          ))}
        </div>
        <p className="mt-3 text-sm text-[#57534E]">Profile status: <strong className="text-[#0A0A0A]">{configured[bankType] ? "Saved" : "Not saved"}</strong></p>
      </div>

      <form onSubmit={saveProfile} className="space-y-5 border-t border-black/15 pt-7" data-testid="bank-details-profile-form">
        <h2 className="font-display text-2xl font-extrabold">Stored details</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {PROFILES[bankType].map(([key, label]) => (
            <label key={key} className="text-sm font-bold">
              {label}
              <input required autoComplete="off" value={values[key]} onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))} className={fieldClass} data-testid={`bank-detail-${key}`} />
            </label>
          ))}
        </div>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[#0A0A0A] px-4 py-3 text-sm font-bold text-white disabled:opacity-50" data-testid="bank-details-save">
          <LockKeyhole className="h-4 w-4" />Save encrypted profile
        </button>
      </form>

      <form onSubmit={sendLink} className="space-y-5 border-t border-black/15 pt-7" data-testid="bank-details-send-form">
        <h2 className="font-display text-2xl font-extrabold">Send a secure link</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-bold">Recipient full name
            <input required maxLength={160} autoComplete="name" value={recipient.full_name} onChange={(event) => setRecipient((current) => ({ ...current, full_name: event.target.value }))} className={fieldClass} data-testid="bank-details-recipient-name" />
          </label>
          <label className="text-sm font-bold">Recipient email
            <input required type="email" autoComplete="email" value={recipient.email} onChange={(event) => setRecipient((current) => ({ ...current, email: event.target.value }))} className={fieldClass} data-testid="bank-details-recipient-email" />
          </label>
        </div>
        <button type="submit" disabled={busy || !configured[bankType]} className="inline-flex items-center gap-2 bg-[#E62314] px-4 py-3 text-sm font-bold text-white disabled:opacity-50" data-testid="bank-details-send-link">
          <Send className="h-4 w-4" />Send secure link
        </button>
      </form>
    </section>
  );
}

export default function BankDetailsPage() {
  const [token, setToken] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [ready, setReady] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.history.replaceState({}, "", window.location.pathname);
    setReady(true);
  }, []);

  const reveal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bank-details/reveal", { token });
      setDetails(data);
      setToken("");
    } catch (requestError) {
      setError(errText(requestError, "This link has expired or has already been used."));
      setToken("");
    } finally {
      setBusy(false);
    }
  };

  const fields = details ? PROFILES[details.bank_type] : [];

  return (
    <main className="min-h-screen bg-[#FBF9F5] px-5 py-12 sm:px-8" data-testid="bank-details-viewer">
      <div className="mx-auto max-w-xl">
        <div className="flex items-center gap-3 border-b border-black/15 pb-5">
          <Landmark className="h-6 w-6 text-[#E62314]" />
          <p className="font-display text-lg font-extrabold">Cresway Media</p>
        </div>
        {!details ? (
          <section className="pt-10">
            <h1 className="font-display text-3xl font-extrabold">Bank details</h1>
            {error ? <p className="mt-5 border-l-2 border-[#E62314] pl-3 text-sm" role="alert">{error}</p> : (
              <>
                <p className="mt-3 text-sm text-[#57534E]">This private link can be opened once.</p>
                {ready && token && <button type="button" onClick={reveal} disabled={busy} className="mt-7 inline-flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white disabled:opacity-50" data-testid="bank-details-reveal">
                  <LockKeyhole className="h-4 w-4" />{busy ? "Opening..." : "View bank details"}
                </button>}
                {ready && !token && <p className="mt-5 text-sm" role="alert">This link is invalid or has expired.</p>}
              </>
            )}
          </section>
        ) : (
          <section className="pt-10">
            <h1 className="font-display text-3xl font-extrabold">{details.bank_type === "domestic" ? "Domestic bank details" : "International bank details"}</h1>
            <dl className="mt-7 divide-y divide-black/15 border-y border-black/15">
              {fields.map(([key, label]) => (
                <div key={key} className="grid gap-1 py-4 sm:grid-cols-[minmax(9rem,0.7fr)_1fr] sm:gap-5">
                  <dt className="text-xs font-bold uppercase text-[#57534E]">{label}</dt>
                  <dd className="break-words text-sm font-semibold" data-testid={`bank-detail-value-${key}`}>{details.details[key]}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </div>
    </main>
  );
}